import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import { PATH_DASHBOARD } from "../../routes/paths";
import {
  approveAccessRequest,
  bulkApproveAccessRequests,
  bulkRejectAccessRequests,
  getAccessInviteStatus,
  listAccessRequests,
  normalizeAccessRequestItem,
  reEnableAccessInvite,
  rejectAccessRequest,
  revokeAccessInvite,
  type RecordAccessInviteStatus,
  type RecordAccessRequestItem,
} from "../../services/record.service";
import { formatInviteStatusLabel } from "./accessLabels";

type ConfirmDialog = "revoke" | "re-enable" | null;

export function useRecordAccessActions(recordId: number | null, enabled: boolean) {
  const { enqueueSnackbar } = useSnackbar();
  const [accessRequests, setAccessRequests] = useState<RecordAccessRequestItem[]>([]);
  const [accessRequestsLoaded, setAccessRequestsLoaded] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<RecordAccessInviteStatus | null>(null);
  const [actingRequestId, setActingRequestId] = useState<number | null>(null);
  const [bulkActing, setBulkActing] = useState(false);
  const [inviteActionLoading, setInviteActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog>(null);

  const loadOwnerAccessRequests = useCallback(async (id: number) => {
    setAccessRequestsLoaded(false);
    try {
      const response = await listAccessRequests(id);
      const items = (response.data?.data ?? []).map(normalizeAccessRequestItem);
      setAccessRequests(items);
    } catch (error: any) {
      setAccessRequests([]);
      if (error?.response?.status !== 403) {
        console.error("Failed to load access requests:", error);
      }
    } finally {
      setAccessRequestsLoaded(true);
    }
  }, []);

  const loadInviteStatus = useCallback(async (id: number) => {
    try {
      const response = await getAccessInviteStatus(id);
      setInviteStatus(response.data?.data ?? null);
    } catch {
      setInviteStatus(null);
    }
  }, []);

  const reload = useCallback(async () => {
    if (!recordId || !enabled) return;
    await Promise.all([loadOwnerAccessRequests(recordId), loadInviteStatus(recordId)]);
  }, [recordId, enabled, loadOwnerAccessRequests, loadInviteStatus]);

  useEffect(() => {
    if (!recordId || !enabled) {
      setAccessRequests([]);
      setAccessRequestsLoaded(false);
      setInviteStatus(null);
      return;
    }
    void reload();
  }, [recordId, enabled, reload]);

  const handleApproveRequest = async (requestId: number) => {
    if (!recordId) return;
    setActingRequestId(requestId);
    try {
      await approveAccessRequest(requestId);
      enqueueSnackbar("Erişim isteği onaylandı.", { variant: "success" });
      await loadOwnerAccessRequests(recordId);
    } catch {
      enqueueSnackbar("Onaylama başarısız.", { variant: "error" });
    } finally {
      setActingRequestId(null);
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    if (!recordId) return;
    setActingRequestId(requestId);
    try {
      await rejectAccessRequest(requestId);
      enqueueSnackbar("Erişim isteği reddedildi.", { variant: "warning" });
      await loadOwnerAccessRequests(recordId);
    } catch {
      enqueueSnackbar("Reddetme başarısız.", { variant: "error" });
    } finally {
      setActingRequestId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!recordId) return;
    setBulkActing(true);
    try {
      const response = await bulkApproveAccessRequests(recordId);
      const count = response?.data?.data?.processed_count ?? 0;
      enqueueSnackbar(`${count} istek onaylandı.`, { variant: "success" });
      await loadOwnerAccessRequests(recordId);
    } catch {
      enqueueSnackbar("Toplu onaylama başarısız.", { variant: "error" });
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkReject = async () => {
    if (!recordId) return;
    setBulkActing(true);
    try {
      const response = await bulkRejectAccessRequests(recordId);
      const count = response?.data?.data?.processed_count ?? 0;
      enqueueSnackbar(`${count} istek reddedildi.`, { variant: "warning" });
      await loadOwnerAccessRequests(recordId);
    } catch {
      enqueueSnackbar("Toplu reddetme başarısız.", { variant: "error" });
    } finally {
      setBulkActing(false);
    }
  };

  const handleConfirmInviteAction = async () => {
    if (!recordId || !confirmDialog) return;
    setInviteActionLoading(true);
    try {
      if (confirmDialog === "revoke") {
        await revokeAccessInvite(recordId);
        enqueueSnackbar("Erişim linki iptal edildi.", { variant: "success" });
      } else {
        await reEnableAccessInvite(recordId);
        enqueueSnackbar("Erişim linki yeniden etkinleştirildi.", { variant: "success" });
      }
      await loadInviteStatus(recordId);
      await loadOwnerAccessRequests(recordId);
    } catch {
      enqueueSnackbar("İşlem başarısız.", { variant: "error" });
    } finally {
      setInviteActionLoading(false);
      setConfirmDialog(null);
    }
  };

  return {
    accessRequests,
    accessRequestsLoaded,
    inviteStatus,
    actingRequestId,
    bulkActing,
    inviteActionLoading,
    confirmDialog,
    setConfirmDialog,
    loadOwnerAccessRequests,
    loadInviteStatus,
    reload,
    handleApproveRequest,
    handleRejectRequest,
    handleBulkApprove,
    handleBulkReject,
    handleConfirmInviteAction,
  };
}

type RecordAccessInviteCardProps = {
  inviteStatus: RecordAccessInviteStatus;
  inviteActionLoading: boolean;
  confirmDialog: ConfirmDialog;
  onSetConfirmDialog: (value: ConfirmDialog) => void;
  onConfirmInviteAction: () => void;
  /** When set, shows a link to the access-audit detail page for this record (e.g. from FileDetail). */
  recordId?: number | null;
};

export function RecordAccessInviteCard({
  inviteStatus,
  inviteActionLoading,
  confirmDialog,
  onSetConfirmDialog,
  onConfirmInviteAction,
  recordId,
}: RecordAccessInviteCardProps) {
  const inviteStatusLabel = formatInviteStatusLabel(inviteStatus);

  return (
    <>
      <Card sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="h6">Erişim davet linki</Typography>
            {recordId != null && !Number.isNaN(recordId) ? (
              <Button
                component={RouterLink}
                to={PATH_DASHBOARD.reports.auditDetail(recordId)}
                size="small"
                variant="outlined"
              >
                Erişim isteklerini yönet
              </Button>
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Durum: <strong>{inviteStatusLabel}</strong>
            {inviteStatus.has_active_invite && inviteStatus.expires_at ? (
              <> — geçerlilik: {new Date(inviteStatus.expires_at).toLocaleString("tr-TR")}</>
            ) : null}
          </Typography>
          <Stack direction="row" spacing={1}>
            {!inviteStatus.revoked ? (
              <Button
                variant="outlined"
                color="error"
                disabled={inviteActionLoading}
                onClick={() => onSetConfirmDialog("revoke")}
              >
                Linki iptal et
              </Button>
            ) : (
              <Button
                variant="contained"
                disabled={inviteActionLoading}
                onClick={() => onSetConfirmDialog("re-enable")}
              >
                Yeniden etkinleştir
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>

      <Dialog
        open={confirmDialog !== null}
        onClose={() => !inviteActionLoading && onSetConfirmDialog(null)}
      >
        <DialogTitle>
          {confirmDialog === "revoke"
            ? "Erişim linkini iptal et"
            : "Erişim linkini yeniden etkinleştir"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog === "revoke"
              ? "Erişim linki geçersiz olacak ve bekleyen tüm istekler reddedilecek. Devam etmek istiyor musunuz?"
              : "Yeni bir erişim linki oluşturulacak; eski e-postalardaki linkler artık çalışmayacak. Devam etmek istiyor musunuz?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onSetConfirmDialog(null)} disabled={inviteActionLoading}>
            Vazgeç
          </Button>
          <Button
            onClick={onConfirmInviteAction}
            color={confirmDialog === "revoke" ? "error" : "primary"}
            variant="contained"
            disabled={inviteActionLoading}
          >
            {inviteActionLoading ? <CircularProgress size={20} color="inherit" /> : "Onayla"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
